import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";

import { CompanyIdentitiesDocument } from "@/graphql/generated/operations";
import type { CompanyLogoValue } from "@/pages/company/company-logo";

/**
 * The most ids one hydration round may carry — genesis's `CompanyController.MAX_COMPANY_IDENTITIES`,
 * mirrored. Over it the server refuses the WHOLE call rather than truncating, so this is a hard
 * boundary and not a tuning knob: raising it here alone turns every logo on the page off at once.
 */
const MAX_HYDRATED_IDS = 50;

/**
 * Logos for a set of company ids the caller already holds, hydrated in capped rounds.
 *
 * <p><b>Why a hydration step and not a `logo` field on the list.</b> The bytes travel base64 inside
 * the GraphQL response (the BFF's CSP admits `data:` but not the blob store's origin), so selecting
 * `logo` on a list whose length the caller did not settle ships one full image per row. genesis
 * therefore resolves `Company.logo` on exactly four surfaces — `company`, `myCompanies`, a single
 * shared item's company, and `companyIdentities` — and `companyIdentities` is the one built for this
 * shape: ids in, name and logo out, server-capped. A list that wants logos asks it, rather than
 * growing a fifth surface.
 *
 * <p><b>It drives itself across rounds.</b> `pending` is what is still unknown, capped at the
 * server's own number; when a round lands the map grows and `pending` recomputes, so a set larger
 * than the cap hydrates in several rounds without a loop or a counter. Every id ASKED for is
 * recorded, not only the ones that came back — an id genesis did not answer for (a company deleted
 * between the two reads) would otherwise stay pending forever and re-issue the query on every
 * render.
 *
 * <p><b>A missing logo and an in-flight one are deliberately the same value: `null`.</b> Both render
 * the monogram, and a row that told them apart would flicker from a placeholder into an image.
 *
 * <p><b>[nameOf] keeps the two apart for a different question.</b> Not "which logo", but "did
 * genesis answer for this id at all" — which is the only thing that distinguishes a company the
 * reader may open from one they may not, or one that does not exist. The assistant's company search
 * reaches further than the reader does, so a caller that turns an id into a link needs this, and the
 * logo's deliberate silence will not give it. It is undefined while a round is in flight, so a
 * caller must read it as "not yet", never as "no". The name comes with the answer either way, and a
 * caller that has one from elsewhere should prefer this one: it is genesis's.
 *
 * <p><b>A failed hydration is silent.</b> The rows are complete without logos — the monogram is the
 * specified fallback (the company page) — so an error here must not take a
 * list down. It is reported for a caller that wants it and otherwise ignored.
 *
 * <p>The same loop is written inline in `use-global-company-search.ts`, which predates this hook and
 * still carries its own copy; that one should adopt this when it is next touched.
 */
export function useCompanyLogos(
  companyIds: readonly string[],
  options: { skip?: boolean } = {},
): {
  logoOf: (companyId: string) => CompanyLogoValue | null;
  /** genesis's own name for this id, or undefined when it has not answered — see the note above. */
  nameOf: (companyId: string) => string | undefined;
  loading: boolean;
  error: Error | undefined;
} {
  const skip = options.skip ?? false;
  const [logos, setLogos] = useState<ReadonlyMap<string, CompanyLogoValue | null>>(new Map());
  // The ids genesis ANSWERED for, with its name for each. `logos` cannot say this: it records every
  // id ASKED, mapping an unanswered one to the same `null` a company without a logo gets.
  const [names, setNames] = useState<ReadonlyMap<string, string>>(new Map());

  const pending = useMemo(
    () => companyIds.filter((id) => !logos.has(id)).slice(0, MAX_HYDRATED_IDS),
    [companyIds, logos],
  );

  const identities = useQuery(CompanyIdentitiesDocument, {
    variables: { input: { ids: pending } },
    // Nothing to ask before there are ids, and nothing to ask once every id is known — an empty
    // list would be a request that can only come back empty.
    skip: skip || pending.length === 0,
  });

  const answered = identities.data?.companyIdentities.companies;
  useEffect(() => {
    if (!answered) return;

    setLogos((previous) => {
      const next = new Map(previous);
      for (const id of pending) next.set(id, null);
      for (const company of answered) next.set(company.id, company.logo ?? null);

      return next;
    });
    setNames((previous) => {
      const next = new Map(previous);
      for (const company of answered) next.set(company.id, company.name);

      return next;
    });
    // `pending` is intentionally not a dependency: this runs on an ANSWER, and re-running it when
    // the ask changes would record the new ask as hydrated before it has been.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answered]);

  return {
    logoOf: (companyId: string) => logos.get(companyId) ?? null,
    nameOf: (companyId: string) => names.get(companyId),
    loading: identities.loading,
    error: identities.error,
  };
}
